import React from "react";
import styled from "styled-components/macro";
import axios from "axios";
import { useSearchParams, useNavigate } from "react-router-dom";
import { TextField, FormControl, InputLabel, Input, InputAdornment, IconButton } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useIntl } from "react-intl";
import translate from "../providers/i18n/translate";
import { ThemeContext } from "../providers/theme";
import { API_URL_UM } from "../config";

const Root = styled.form`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 80vw;
    height: auto;
    margin: 2rem auto;
    @media (min-width: 1024px) {
        width: 350px;
        min-height: 300px;
    }
    input:-webkit-autofill {
        transition: background-color 5000s ease-in-out 0s, color 5000s ease-in-out 0s;
        transition-delay: background-color 5000s, color 5000s;
    }
`

const Title = styled.h2`
    width: 80%;
    border-bottom: 1px solid rgba(0,0,0,0.8);
    text-transform: uppercase;
    text-align: center;
    line-height: 3rem;
    margin: 0 0 30px 0;
`

const StyledButton = styled.button`
    border-radius: 47px;
    background: ${(props) => props.bg ? props.bg : '#D2A757'};
    padding: 10px 30px;
    margin: 30px 0;
    box-shadow: none;
    user-select: none;
    outline: none;
    border: none;
    cursor: pointer;
    color: white;
`

const MessageText = styled.div`
    margin-top: 16px;
    text-align: center;
    font-size: 0.875rem;
`

const ErrorText = styled.p`
    color: #b00020;
    font-size: 0.875rem;
    margin: 0.5rem 0 0 0;
    text-align: center;
`

const PasswordRequirements = styled.div`
    margin-top: 8px;
    text-align: center;
    font-size: 0.875rem;
    color: #555;
`

export default function ResetPasswordPage() {
    const intl = useIntl();
    const theme = React.useContext(ThemeContext);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get("token") || "";

    const [ newPassword, setNewPassword ] = React.useState("");
    const [ confirm, setConfirm ] = React.useState("");
    const [ showPassword, setShowPassword ] = React.useState(false);
    const [ submitting, setSubmitting ] = React.useState(false);
    const [ error, setError ] = React.useState("");

    const submit = (e) => {
        e.preventDefault();
        setError("");
        if (!token.trim()) {
            alert(intl.formatMessage({ id: "resetPasswordMissingToken" }));
            return;
        }
        if (newPassword !== confirm) {
            alert(intl.formatMessage({ id: "resetPasswordMismatch" }));
            return;
        }
        setSubmitting(true);
        axios
            .post(`${API_URL_UM}/api/users/reset-password`, {
                token: token.trim(),
                newPassword,
            })
            .then(() => {
                navigate("/login", { replace: true, state: { backgroundLocation: { pathname: "/" } } });
            })
            .catch((err) => {
                setError(err.response?.data?.msg || intl.formatMessage({ id: "resetPasswordGenericError" }));
            })
            .finally(() => setSubmitting(false));
    };

    if (!token.trim()) {
        return (
            <Root onSubmit={(e) => e.preventDefault()}>
                <Title>{translate("newpassword")}</Title>
                <MessageText>{translate("resetPasswordMissingToken")}</MessageText>
                <StyledButton type="button" onClick={() => navigate("/")} bg={theme.palette.accent}>
                    {translate("home")}
                </StyledButton>
            </Root>
        );
    }

    return (
        <Root onSubmit={submit}>
            <Title>{translate("newpassword")}</Title>
            <TextField
                label={translate("newpassword")}
                name="newPassword"
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                variant="standard"
                margin="normal"
                fullWidth
                autoComplete="new-password"
            />
            <FormControl variant="standard" fullWidth margin="normal">
                <InputLabel htmlFor="reset-confirm-password">{translate("repassword")}</InputLabel>
                <Input
                    id="reset-confirm-password"
                    name="confirm"
                    type={showPassword ? "text" : "password"}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    autoComplete="new-password"
                    endAdornment={
                        <InputAdornment position="end">
                            <IconButton aria-label="toggle password visibility" onClick={() => setShowPassword(!showPassword)}>
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                        </InputAdornment>
                    }
                />
            </FormControl>
            <PasswordRequirements>{translate("passwordRequirements")}</PasswordRequirements>
            {error ? <ErrorText>{error}</ErrorText> : null}
            <StyledButton type="submit" disabled={submitting} bg={theme.palette.accent}>
                {translate("resetPasswordSubmit")}
            </StyledButton>
        </Root>
    );
}
